import { SocketEvents } from '@sea-snag/shared';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';

import { getAccessToken } from '../lib/api-client';
import {
  type ConnectionStatus,
  connectSocket,
  disconnectSocket,
  getSocket,
  type ServerToClientEvents,
  type SocketError,
  type TypedSocket,
} from '../lib/socket-client';

export interface SocketContextValue {
  socket: TypedSocket | null;
  status: ConnectionStatus;
  error: SocketError | null;
  connect: () => void;
  disconnect: () => void;
  isConnected: boolean;
}

export const SocketContext = createContext<SocketContextValue | null>(null);

interface SocketProviderProps {
  children: React.ReactNode;
  autoConnect?: boolean;
}

export function SocketProvider({ children, autoConnect = true }: SocketProviderProps) {
  const [socket, setSocket] = useState<TypedSocket | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [error, setError] = useState<SocketError | null>(null);

  const connect = useCallback(() => {
    const accessToken = getAccessToken();
    if (!accessToken) {
      setError({ message: 'No access token available' });
      setStatus('error');
      return;
    }

    setStatus('connecting');
    setError(null);

    const socketInstance = connectSocket(accessToken);
    setSocket(socketInstance);
  }, []);

  const disconnect = useCallback(() => {
    disconnectSocket();
    setSocket(null);
    setStatus('disconnected');
    setError(null);
  }, []);

  // Set up socket event listeners
  useEffect(() => {
    const currentSocket = socket ?? getSocket();
    if (!currentSocket) return;

    const handleConnect = () => {
      setStatus('connected');
      setError(null);
    };

    const handleDisconnect = (reason: string) => {
      setStatus('disconnected');
      if (reason === 'io server disconnect') {
        // Server initiated disconnect, don't auto-reconnect
        setError({ message: 'Disconnected by server', code: 'SERVER_DISCONNECT' });
      }
    };

    const handleConnectError = (err: Error) => {
      setStatus('error');
      setError({ message: err.message, code: 'CONNECT_ERROR' });
    };

    const handleAuthSuccess = () => {
      setStatus('connected');
      setError(null);
    };

    const handleAuthError: ServerToClientEvents[typeof SocketEvents.AUTH_ERROR] = (authError) => {
      setStatus('error');
      setError(authError);
      currentSocket.disconnect();
    };

    currentSocket.on('connect', handleConnect);
    currentSocket.on('disconnect', handleDisconnect);
    currentSocket.on('connect_error', handleConnectError);
    currentSocket.on(SocketEvents.AUTH_SUCCESS, handleAuthSuccess);
    currentSocket.on(SocketEvents.AUTH_ERROR, handleAuthError);

    return () => {
      currentSocket.off('connect', handleConnect);
      currentSocket.off('disconnect', handleDisconnect);
      currentSocket.off('connect_error', handleConnectError);
      currentSocket.off(SocketEvents.AUTH_SUCCESS, handleAuthSuccess);
      currentSocket.off(SocketEvents.AUTH_ERROR, handleAuthError);
    };
  }, [socket]);

  // Auto-connect when component mounts (if autoConnect is true and token exists)
  useEffect(() => {
    if (autoConnect) {
      const accessToken = getAccessToken();
      if (accessToken) {
        connect();
      }
    }

    return () => {
      disconnect();
    };
  }, [autoConnect, connect, disconnect]);

  const value = useMemo(
    () => ({
      socket,
      status,
      error,
      connect,
      disconnect,
      isConnected: status === 'connected',
    }),
    [socket, status, error, connect, disconnect]
  );

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}
